import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Loader2, Search, Shield, UserRound, Users, X } from "lucide-react";
import {
  getDelegationCandidates,
  type DelegationMode,
} from "@/lib/admin-api";
import { useDelegation } from "@/lib/delegation";
import {
  useDialogFocusTrap,
  useLowerWorkspaceDialogContainment,
} from "@/lib/lower-workspace-dialog";

export function DelegationStartButtons({
  subjectUserId,
  role,
  compact = false,
}: {
  subjectUserId: number;
  role: string;
  compact?: boolean;
}) {
  const { begin, isChanging } = useDelegation();
  const [, setLocation] = useLocation();
  const start = (mode: DelegationMode) => {
    void begin(subjectUserId, mode).then(() => {
      setLocation(mode === "coach" ? "/coach/dashboard" : "/dashboard");
    });
  };
  const buttonClass = compact
    ? "rounded-md border border-amber-300/30 px-2 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-300/10 disabled:opacity-50"
    : "inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-amber-300/30 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-300/10 disabled:opacity-50";
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Delegated view actions">
      {role !== "admin" && (
        <button
          type="button"
          data-testid={`button-view-client-${subjectUserId}`}
          disabled={isChanging}
          onClick={() => start("client")}
          className={buttonClass}
        >
          {isChanging && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          View as Client
        </button>
      )}
      {role === "coach" && (
        <button
          type="button"
          data-testid={`button-view-coach-${subjectUserId}`}
          disabled={isChanging}
          onClick={() => start("coach")}
          className={buttonClass}
        >
          View as Coach
        </button>
      )}
    </div>
  );
}

function CoachChooser({ onClose }: { onClose: () => void }) {
  const [search, setSearch] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const containment = useLowerWorkspaceDialogContainment();
  const focusTrap = useDialogFocusTrap(panelRef, onClose);
  const { begin, isChanging, error } = useDelegation();
  const [, setLocation] = useLocation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "delegation", "candidates", search],
    queryFn: () => getDelegationCandidates(search, "coach"),
    enabled: search.trim().length >= 2,
  });
  const candidates = data?.candidates ?? [];
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 p-4"
      style={containment.overlayStyle}
      onKeyDown={focusTrap.onKeyDown}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        className="max-h-full w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#0a0a12] p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="coach-mode-title"
        tabIndex={-1}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 id="coach-mode-title" className="text-lg font-semibold text-white">Choose a Coach account</h2>
          <button type="button" data-testid="button-close-coach-chooser" aria-label="Close" onClick={onClose} className="rounded-md p-2 text-white/50 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <label className="relative mt-4 block">
          <span className="sr-only">Search Coach accounts</span>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
          <input
            data-testid="input-coach-candidate-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search pseudonymous ID"
            className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-2 pl-9 pr-3 text-sm text-white focus:border-primary/50 focus:outline-none"
          />
        </label>
        <div className="mt-3 max-h-72 overflow-y-auto">
          {search.trim().length < 2 ? (
            <p className="py-6 text-center text-sm text-white/40">
              Enter at least 2 characters to search.
            </p>
          ) : isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : isError ? (
            <p role="alert" className="py-6 text-center text-sm text-rose-300">Could not load eligible Coach accounts.</p>
          ) : candidates.filter((candidate) => candidate.modes?.includes("coach") || candidate.role === "coach").length === 0 ? (
            <p className="py-6 text-center text-sm text-white/40">No eligible Coach accounts found.</p>
          ) : (
            <ul className="space-y-2">
              {candidates
                .filter((candidate) => candidate.modes?.includes("coach") || candidate.role === "coach")
                .map((candidate) => (
                  <li key={candidate.id}>
                    <button
                      type="button"
                      data-testid={`button-select-coach-${candidate.id}`}
                      disabled={isChanging}
                      onClick={() => void begin(candidate.id, "coach").then(() => setLocation("/coach/dashboard"))}
                      className="flex min-h-11 w-full items-center justify-between rounded-lg border border-white/10 px-3 text-left text-sm text-white/80 hover:border-primary/40 hover:bg-white/[0.04] disabled:opacity-50"
                    >
                      <span className="font-mono">{candidate.pseudonym}</span>
                      <span className="text-xs text-primary">Open Coach mode</span>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>
        {error && <p role="alert" className="mt-3 text-xs text-rose-300">{error}</p>}
      </div>
    </div>
  );
}

export function AdminModeControl({ mobile = false }: { mobile?: boolean }) {
  const [coachOpen, setCoachOpen] = useState(false);
  const itemClass = "flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-lg px-2 text-xs font-semibold transition-colors";
  return (
    <>
      <nav aria-label="Experience mode" data-testid={mobile ? "mobile-mode-control" : "desktop-mode-control"} className="flex gap-1 rounded-xl border border-white/10 bg-white/[0.03] p-1">
        <Link data-testid="link-mode-admin" href="/admin" className={`${itemClass} bg-primary/15 text-primary`}>
          <Shield className="h-3.5 w-3.5" /> Admin
        </Link>
        <Link data-testid="link-mode-profile" href="/profile" className={`${itemClass} text-white/60 hover:bg-white/[0.06] hover:text-white`}>
          <UserRound className="h-3.5 w-3.5" /> My Profile
        </Link>
        <button type="button" data-testid="button-mode-coach" onClick={() => setCoachOpen(true)} className={`${itemClass} text-white/60 hover:bg-white/[0.06] hover:text-white`}>
          <Users className="h-3.5 w-3.5" /> Coach
        </button>
      </nav>
      {coachOpen && <CoachChooser onClose={() => setCoachOpen(false)} />}
    </>
  );
}